import { Button, DataTable } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Toast } from '../../../shared/components/Toast';
import { resumenAPI, obrasSocialesAPI, centrosAPI, tiposEstudiosAPI, medicosAPI, derivadoresAPI, categoriasAPI } from '../../../core/api/api';
import '../../contabilidad/components/ContabilidadPage.css';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Setiembre','Octubre','Noviembre','Diciembre'];
const ANIOS = Array.from({ length: 10 }, (_, i) => String(2018 + i));

const TablaResumen = ({ titulo, icono, datos, colKey, colLabel }) => (
  <div style={{ marginBottom: '1.5rem', breakInside: 'avoid' }}>
    <div className="cont-section-header">
      <span className="cont-section-icon">{icono}</span>
      <h3 className="cont-section-title">{titulo}</h3>
      <span className="cont-section-badge">{datos?.length ?? 0} registros</span>
    </div>
    <div className="cont-tabla-wrap" style={{ borderRadius: '0 0 12px 12px' }}>
      <DataTable className="cont-tabla">
        <thead>
          <tr>
            <th>{colLabel}</th>
            <th style={{ textAlign: 'right' }}>Cantidad</th>
          </tr>
        </thead>
        <tbody>
          {!datos?.length ? (
            <tr><td colSpan="2" style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>Sin datos</td></tr>
          ) : datos.map((row, i) => (
            <tr key={i}>
              <td>{row[colKey]}</td>
              <td className="cont-td-num">{row.cantidad}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  </div>
);

const GrillaResumen = () => {
  const hoy = new Date();
  const [filtros, setFiltros] = useState({
    mes: String(hoy.getMonth() + 1),
    anio: String(hoy.getFullYear()),
    idObraSocial: '', idCentro: '', idTipoEstudio: '',
    idMedico: '', idDerivador: '', especialidad: '', idCategoria: '',
  });
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [obrasSociales, setObrasSociales] = useState([]);
  const [centros, setCentros] = useState([]);
  const [tiposEstudios, setTiposEstudios] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [derivadores, setDerivadores] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [categorias, setCategorias] = useState([]);

  useEffect(() => {
    Promise.all([
      obrasSocialesAPI.getObrasSociales(),
      centrosAPI.getCentros(),
      tiposEstudiosAPI.getTiposEstudios(),
      medicosAPI.getMedicos(),
      derivadoresAPI.getDerivadores(),
      categoriasAPI.getCategorias(),
    ]).then(([os, c, te, m, d, cat]) => {
      setObrasSociales(os.data || []);
      setCentros(c.data || []);
      setTiposEstudios(te.data || []);
      setMedicos(m.data || []);
      const derivs = d.data || [];
      setDerivadores(derivs);
      setEspecialidades([...new Set(derivs.map(x => x.especialidad).filter(Boolean))].sort());
      setCategorias(cat.data || []);
    }).catch(() => {});
  }, []);

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        mes: filtros.mes, anio: filtros.anio,
        ...(filtros.idObraSocial && { idObraSocial: filtros.idObraSocial }),
        ...(filtros.idCentro && { idCentro: filtros.idCentro }),
        ...(filtros.idTipoEstudio && { idTipoEstudio: filtros.idTipoEstudio }),
        ...(filtros.idMedico && { idMedico: filtros.idMedico }),
        ...(filtros.idDerivador && { idDerivador: filtros.idDerivador }),
        ...(filtros.especialidad && { especialidad: filtros.especialidad }),
        ...(filtros.idCategoria && { idCategoria: filtros.idCategoria }),
      };
      const res = await resumenAPI.getResumen(params);
      setDatos(res.data);
    } catch {
      setToast({ message: 'Error al cargar el resumen.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { buscar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const limpiar = () => setFiltros(f => ({
    ...f, idObraSocial: '', idCentro: '', idTipoEstudio: '',
    idMedico: '', idDerivador: '', especialidad: '', idCategoria: '',
  }));

  const f = (k) => (e) => setFiltros(p => ({ ...p, [k]: e.target.value }));

  const sel = (label, key, opts, valKey, lblKey) => (
    <div className="form-group">
      <label>{label}</label>
      <select className="filter-input" value={filtros[key]} onChange={f(key)}>
        <option value="">{label}...</option>
        {opts.map(o => <option key={o[valKey]} value={o[valKey]}>{o[lblKey]}</option>)}
      </select>
    </div>
  );

  const mesPeriodo = MESES[Number(filtros.mes) - 1];

  return (
    <div className="contabilidad-page">
      <Header />
      <div className="contabilidad-content">

        <div className="page-header">
          <h2 className="page-title-grilla">📊 Resumen</h2>
          <span className="page-subtitle">Estadísticas de turnos y estudios — {mesPeriodo} {filtros.anio}</span>
        </div>

        <div className="filtros-container">
          <div className="form-group">
            <label>Mes</label>
            <select className="filter-input" value={filtros.mes} onChange={f('mes')}>
              {MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Año</label>
            <select className="filter-input" value={filtros.anio} onChange={f('anio')}>
              {ANIOS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          {sel('Obra Social', 'idObraSocial', obrasSociales, 'idObraSocial', 'obraSocial')}
          {sel('Centro', 'idCentro', centros, 'idCentro', 'centro')}
          {sel('Práctica', 'idTipoEstudio', tiposEstudios, 'idTipoEstudio', 'tipoEstudio')}
          {sel('Profesional', 'idMedico', medicos, 'idMedico', 'medico1')}
          {sel('Derivador', 'idDerivador', derivadores, 'idDerivador', 'derivador')}
          <div className="form-group">
            <label>Especialidad</label>
            <select className="filter-input" value={filtros.especialidad} onChange={f('especialidad')}>
              <option value="">Especialidad...</option>
              {especialidades.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          {sel('Categoría', 'idCategoria', categorias, 'idCategoria', 'categoria1')}
          <div className="form-group button-group">
            <Button onClick={buscar} className="btn btn-primary" disabled={loading}>Buscar</Button>
            <Button onClick={limpiar} className="btn" style={{ background: '#f1f5f9', color: '#475569', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Limpiar</Button>
          </div>
        </div>

        {loading && (
          <div className="contabilidad-empty">
            <div className="empty-icon">⏳</div>
            <p>Cargando datos...</p>
          </div>
        )}

        {!datos && !loading && (
          <div className="contabilidad-empty">
            <div className="empty-icon">📋</div>
            <p>Seleccioná un período y presioná <strong>Buscar</strong> para ver el resumen.</p>
          </div>
        )}

        {datos && !loading && (
          <>
            {/* Totales */}
            <div className="totales-container">
              <div className="card-total card-resultado">
                <span className="card-icon">📅</span>
                <h4>Estudios Agendados</h4>
                <strong>{datos.agendados}</strong>
              </div>
              <div className="card-total card-ingresos">
                <span className="card-icon">✅</span>
                <h4>Estudios Realizados</h4>
                <strong>{datos.realizados}</strong>
              </div>
              {datos.agendados > 0 && (
                <div className="card-total card-pendiente">
                  <span className="card-icon">📈</span>
                  <h4>% Realización</h4>
                  <strong>{Math.round((datos.realizados / datos.agendados) * 100)}%</strong>
                </div>
              )}
            </div>

            {/* Grillas en 2 columnas */}
            <div style={{ columns: '320px', columnGap: '1.5rem' }}>
              <TablaResumen titulo="Por Centros" icono="🏢" datos={datos.porCentro} colKey="centro" colLabel="Centro" />
              <TablaResumen titulo="Por Estados" icono="🔖" datos={datos.porEstado} colKey="estadoTurno" colLabel="Estado" />
              <TablaResumen titulo="Por Tipos de Estudios" icono="🔬" datos={datos.porTipoEstudio} colKey="tipoEstudio" colLabel="Práctica Médica" />
              <TablaResumen titulo="Por Obra Social" icono="🏥" datos={datos.porObraSocial} colKey="obraSocial" colLabel="Obra Social" />
              <TablaResumen titulo="Por Médicos" icono="👨‍⚕️" datos={datos.porMedico} colKey="medico" colLabel="Médico" />
              <TablaResumen titulo="Por Técnicos" icono="🔧" datos={datos.porTecnico} colKey="medico" colLabel="Técnico" />
              <TablaResumen titulo="Por Categorías" icono="📂" datos={datos.porCategoria} colKey="categoria" colLabel="Categoría" />
              <TablaResumen titulo="Por Localidades de Pacientes" icono="📍" datos={datos.porLocalidad} colKey="localidad" colLabel="Localidad" />
              <TablaResumen titulo="Por Especialidad Derivador" icono="🩺" datos={datos.porEspecialidad} colKey="especialidad" colLabel="Especialidad" />
              <TablaResumen titulo="Por Derivador" icono="👤" datos={datos.porDerivador} colKey="derivador" colLabel="Derivador" />
            </div>
          </>
        )}

      </div>
      <Footer />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaResumen;
